import React, { useState, useEffect} from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom';

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';
import FormField from './FormField';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';

import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import useMediaQuery from '@mui/material/useMediaQuery';
import clsx from 'clsx';
import useStyles from '../styles/Styles';

import _ from 'lodash';
import * as yup from 'yup';
import dayjs from 'dayjs';

import { isTrue, describeDate } from '../utils';

import { fetchAbsences, addAbsence, fetchEnrolments, resetErrorMessage } from '../actions';

const Absences = () => {
	const dispatch = useDispatch();
	const classes = useStyles();
	const isMd = useMediaQuery('(min-width:960px)');
	
	const generalSettings = useSelector(state => state.settings.general);
	const courseLabels = useSelector(state => state.settings.labels_courses.data);
	
	const fetched = useSelector(state => state.absences.expires);
	const errorMsg = useSelector(state => state.errorMessage);
	const submitting = useSelector(state => state.absences.submitting);
	const adding = useSelector(state => state.absences.adding);
	const added = useSelector(state => state.absences.added);
	const addingFailed = useSelector(state => state.absences.failed_to_add);
	
	const absences = useSelector(state => state.absences.data);
	const enrolments = useSelector(state => state.enrolments.data.enrolled, _.isEqual );
	
	const [courseSelectLabel, setCourseSelectLabel] =  useState('Select Course');
	const [courseSelectOptions, setCourseSelectOptions] = useState({'0': 'All Courses'});
	
	const lastWeek = dayjs().subtract(1, 'weeks').format('YYYY-MM-DD');
	const today = dayjs().format("YYYY-MM-DD");
	const twoYears = dayjs().add(2, 'years').format('YYYY-MM-DD');
	
	const [submitted, setSubmitted] = useState(false);
	
	useEffect(() => {
		if(isTrue(generalSettings.data.absences_on)){
			dispatch(fetchAbsences());
			dispatch(fetchEnrolments(today, twoYears));
		}
	}, [dispatch, generalSettings, today, twoYears])
	
	useEffect(() => {
		setCourseSelectLabel('Select ' + courseLabels.courseCapLabel);
	}, [courseLabels])
	
	useEffect(() => {
		const options = _.chain(enrolments).filter(e => {
			return e.finish_date >= today && e.status === 'Enrolled';
		}).sortBy('course_name').keyBy('course_code').mapValues(e => {
			return e.course_code + ': ' + _.truncate(e.course_name, { length: 25 });
		}).value();
		options['0'] = 'All ' + courseLabels.coursesCapLabel;
		setCourseSelectOptions( options );
	}, [enrolments, courseLabels, today])
	
	const AbsencesOff = () => (
		<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
		<PageTitle title='My Absences' />
		<Typography variant="h6">
		Absence management is off
		</Typography>
		<Typography variant="body2">
		Sorry, abscence management is currently disabled. Please check again later, or <Link to="/contact">contact us</Link> for more information.
		</Typography>
		</Paper>
	);

	const [data, setData] = useState({
		course_code: '0',
		start_date: today,
		finish_date: today,
		reason: 'Other',
	});
	
	const [validator, setValidator] = useState({
		course_code: { error: false, message: '' },
		start_date: { error: false, message: '' }, 
		finish_date: { error: false, message: '' },
		reason: { error: false, message: '' },
	});
	
	const schema = yup.object().shape({
		start_date: yup.date().required('Please enter a valid date')
			.min(lastWeek, 'Minimum allowed date is '+describeDate(lastWeek))
			.max(twoYears, 'Maximum allowed date is '+describeDate(twoYears))
			.typeError('Please enter a valid date in the format dd/mm/yyyy'),
		finish_date: yup.date().required('Please enter a valid date')
			.min(today, 'Minimum allowed date is '+describeDate(today))
			.max(twoYears, 'Maximum allowed date is '+describeDate(twoYears))
			.typeError('Please enter a valid date in the format dd/mm/yyyy'),
		reason: yup.string().oneOf(['Other', 'Holiday', 'Illness']).required('Please select a reason'),
		course_code: yup.string()
	});
	
	const storeAndValidate = (key, value) => {
		const updatedData = {...data, [key]: value};
		setData(updatedData);
		
		const v = {
			course_code: { error: false, message: '' },
			start_date: { error: false, message: '' }, 
			finish_date: { error: false, message: '' },
			reason: { error: false, message: '' },
		};
		
		schema.validate(updatedData, {abortEarly: false})
		.then((value) => {
			// Nothing
		}).catch((err) => {
			_.each(err.inner, (e) => {
				v[e.path].error = true;
				v[e.path].message = e.message;
			});
		}).finally(() =>{
			if(!v.start_date.error && !v.finish_date.error && dayjs(updatedData.start_date).isAfter(updatedData.finish_date)){
				v.finish_date = {
					error: true,
					message: 'Finish date must not be earlier than start date'
				}
			}
			setValidator(v);
		})
		
		
	}
	
	const maybeSumit = () => {
		let hasError = false;
		
		const v = {
			course_code: { error: false, message: '' },
			start_date: { error: false, message: '' }, 
			finish_date: { error: false, message: '' },
			reason: { error: false, message: '' },
		};
		
		schema.validate(data, {abortEarly: false})
		.then((value) => {
			// Nothing
		}).catch((err) => {
			hasError = true;
			
			_.each(err.inner, (e) => {
				v[e.path].error = true;
				v[e.path].message = e.message;
			});
		}).finally(() =>{
			if(!v.start_date.error && !v.finish_date.error && dayjs(data.start_date).isAfter(data.finish_date)){
				v.finish_date = {
					error: true,
					message: 'Finish date must not be earlier than start date'
				}
				hasError = true;
			}
			if(hasError){
				setValidator(v);
			} else {
				const sd = dayjs(data.start_date).format('YYYY-MM-DD');
				const fd = dayjs(data.finish_date).format('YYYY-MM-DD');
				const cc = data.course_code === '0' ? null : data.course_code;
				
				if(addingFailed){
					dispatch(resetErrorMessage());
				}
				dispatch(addAbsence(sd, fd, data.reason, cc));
				setSubmitted(true);
			}
		})
	}
	
	const fields = [
		{ 
			id: "course_code",
			type: "select",
			atts: {
				label: courseSelectLabel,
				required: false,
				options: courseSelectOptions
			},
		},
		{ 
			id: "reason",
			type: "select",
			atts: {
				label: "Select Reason",
				required: true,
				options: {
					Other: 'Other',
					Holiday: 'Holiday',
					Illness: 'Illness',
				}
			},
		},
		{
			type: "date",
			id: "start_date",
			atts: {
				label: "Start Date",
				placeholder: "Select start date",
				format:"DD/MM/YYYY",
				required: true,
				minDate: lastWeek,
				maxDate: twoYears,
			},
		},
		{
			type: "date",
			id: "finish_date",
			atts: {
				label: "Finish Date",
				placeholder: "Select finish date",
				format:"DD/MM/YYYY",
				required: true,
				minDate: today,
				maxDate: twoYears,
			},
		},
	];
	
	const AbsencesTable = () => {
		const size= isMd ? "medium" : "small";
		const flexLayout = !isMd;

		const tableFields = {
			course_name: { type: 'text', label: courseLabels.courseCapLabel },
			course_code: { type: 'text', label: courseLabels.courseCapLabel + ' Code' },
			start_date: { type: 'date', label: 'Start Date' },
			finish_date: { type: 'date', label: 'Finish Date' },
			reason: { type: 'text', label: 'Reason' },
		}
		
		return (
		<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
		<Typography variant="h5" component="h2" paragraph>Current Absences</Typography>
		
		{absences.length === 0 ? <Typography paragraph>You have no current or upcoming absences.</Typography> : 
		
		<Table className={classes.enrolmentsTable} size={size}>
		{flexLayout ? null :
		<TableHead>
		<TableRow>
		{_.map(tableFields, (f, k) => (
			<TableCell key={k}>
				{f.label}
			</TableCell>
		))}
		</TableRow>
		</TableHead>
		}
		<TableBody className={flexLayout ? classes.enrolmentsFlexTable : null}>
		{_.map(absences, (a, k) => (
			<TableRow key={k} >
			{_.map(tableFields, (f, k) => (
				<TableCell key={k}>
					{flexLayout ? <strong>{f.label}: </strong> : null}
					{f.type === 'date' ? describeDate(a[k]) : a[k]}
				</TableCell>
			))}
			</TableRow>
		))}
		</TableBody>
		</Table>
		
		}
		
		</Paper>
	)};
	
	const SuccessSnack = () => {
		return <Snackbar
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center'
				}}
				open={submitted && added}
				onClose={() => setSubmitted(false)}
				autoHideDuration={6000}
			>
			<SnackbarContent
				message={<span className={classes.snackMessage}>
					<CheckCircleIcon className={classes.successSnack} />
					Success: Absence(s) registered.
				</span>}
				action={[
					<IconButton
					key="close"
					aria-label="close"
					color="inherit"
					onClick={() => setSubmitted(false)}
					>
					<CloseIcon />
					</IconButton>,
				]}
			/>
		</Snackbar>
	}
	
	return (
	<>
	<ErrorDisplay />
	{generalSettings.expires > 0 && (!isTrue(generalSettings.data.absences_on) || generalSettings.data.umas_status < 1) ? <AbsencesOff /> : (
		<>
		<PageTitle title='My Absences' withoutPaper  />
		{ (fetched === 0 && !errorMsg) || submitting || adding ? 
		<Waiting 
			message='Fetching absences. Please wait...'
			withPaper
		/> : <>
			<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
			<Typography variant="h5" component="h2" paragraph>Submit Absences</Typography>
			<form className={classes.form} autoComplete="off" noValidate> 
			<Grid container spacing={2}>
			{fields.map((f, i) => {
				return <Grid item key={f.id} xs={12} md={6} className={classes.relative} >
				<FormField 
					type={f.type}
					id={f.id}
					value={data[f.id]}
					handleChange={storeAndValidate}
					error={validator[f.id] && validator[f.id].error}
					message={validator[f.id] ? validator[f.id].message : ''}
					{...f.atts}
				/>
				</Grid>
			})}
				<Grid item xs={12}>
					<Button
						type="button"
						fullWidth
						variant="contained"
						color="primary"
						className={classes.submit}
						onClick={() => maybeSumit()}
						>Submit</Button>
				</Grid>
			</Grid>
			</form>
			</Paper>
			<AbsencesTable />
			<SuccessSnack />
		</>
		}
		</>
	)}
	</>
)};

export default Absences